import React, { useEffect, useState } from "react";
import JobCard from "../../templates/JobCard";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { SavedJobs, saveJobs, unSaveJobs } from "../../store/jobSlice";
import AdCard from "../../templates/AdCard";

export default function JobList({
  jobs: jobsProp,
  excludeId = null,
  jobsPerPage = 90,
  adFrequency = 0,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dataFetched, setDataFetched] = useState(false); // 🆕 Added
  const UserId = useSelector((state) => state.auth.user?.$id);

  const loading = useSelector((state) => state.job.loading);
  const backendFilteredJobs = useSelector((state) => state.job.filteredJobs);
  const allJobs = useSelector((state) => state.job.jobs);
  const savedJobsStale = useSelector((state) => state.job.savedJobsStale);
  const savedJobsFromRedux = useSelector((state) => state.job.savedJobs);

  const jobsSource =
    backendFilteredJobs !== null ? backendFilteredJobs : jobsProp ?? allJobs;

  const filteredJobs = excludeId
    ? jobsSource.filter((job) => job.$id !== excludeId)
    : jobsSource;

  // ✅ Mark data fetched when jobs are first available
  useEffect(() => {
    if (filteredJobs.length > 0) {
      setDataFetched(true);
    }
  }, [filteredJobs]);

  useEffect(() => {
    if (UserId && savedJobsStale) {
      dispatch(SavedJobs({ UserId }));
    }
  }, [UserId, savedJobsStale, dispatch]);

  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);

  const handleNext = () =>
    currentPage < totalPages && setCurrentPage((p) => p + 1);
  const handlePrev = () => currentPage > 1 && setCurrentPage((p) => p - 1);

  const handleViewDetails = (jobId) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => navigate(`/job/${jobId}`), 300);
  };

  const handleSaveJobs = async (JobId, isSaved) => {
    if (!UserId) {
      setIsModalOpen(true);
      return;
    }

    if (isSaved) {
      dispatch({ type: "job/saveJobs/fulfilled", payload: { jobId: JobId } });
    } else {
      dispatch({ type: "job/unSaveJobs/fulfilled", payload: { jobId: JobId } });
    }

    try {
      if (isSaved) {
        await dispatch(saveJobs({ UserId, JobId })).unwrap();
      } else {
        await dispatch(unSaveJobs({ UserId, JobId })).unwrap();
      }
    } catch (error) {
      console.error("Failed to save/unsave job:", error);
      if (isSaved) {
        dispatch({
          type: "job/unSaveJobs/fulfilled",
          payload: { jobId: JobId },
        });
      } else {
        dispatch({ type: "job/saveJobs/fulfilled", payload: { jobId: JobId } });
      }
    }
  };

  // 🧠 Don't show “No jobs” until we know data is fetched
  if (!loading && dataFetched && filteredJobs.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No jobs available at the moment.</p>
      </div>
    );
  }

  if (loading && !dataFetched) {
    return (
      <div className="text-center py-8 text-gray-600">
        <p>Loading jobs...</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Job grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {currentJobs.map((job, index) => (
          <React.Fragment key={job.$id}>
            <JobCard
              jobId={job.$id}
              title={job.Title}
              company={job.Company}
              location={
                job.City && job.Country
                  ? `${job.City}, ${job.Country}`
                  : job.City || job.Country || ""
              }
              buttonLabel="View Details"
              onClick={() => handleViewDetails(job.$id)}
              onSave={handleSaveJobs}
              isSaved={UserId ? savedJobsFromRedux.includes(job.$id) : false}
            />

            {(index + 1) % 12 === 0 && adFrequency !== 0 && (
              <div className="col-span-full flex justify-center my-4">
                <AdCard
                  slot="9326257490"
                  style={{
                    display: "block",
                    width: "100%",
                    minHeight: "250px",
                  }}
                  format="fluid"
                  responsive="true"
                />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-6">
          <button
            onClick={handlePrev}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Login Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 p-8 rounded-2xl relative max-w-md w-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 dark:hover:text-white text-2xl font-bold"
            >
              ✕
            </button>
            <div className="text-center space-y-4">
              <p className="text-lg font-medium text-gray-800 dark:text-gray-200">
                Please{" "}
                <Link
                  to="/login"
                  className="text-orange-700 underline rounded-lg hover:text-indigo-900 dark:text-white transition"
                >
                  Login
                </Link>{" "}
                to save jobs.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Don’t have an account?
              </p>
              <Link
                to="/signup"
                className="block w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                Register Here
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
